import React, { useState } from 'react';
import { Icon, Icons, Tooltip, TooltipProvider } from 'folds';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getHomePath } from '../../pages/pathUtils';
import { SettingsModal } from './SettingsModal';
import { useMatrixClient } from '../../hooks/useMatrixClient';
import { useRoomRoleAccessing } from '../../state/room/roomRoleAccessing';
import { confirmDialog } from '../../molecules/confirm-dialog/ConfirmDialog';
import { logoutClient } from '../../../client/initMatrix';
import buildConfig from '../../../../build.config';
import './curve-theme.css';

const NAV_WIDTH = 64;

function isMessagesActive(pathname: string): boolean {
  return pathname.startsWith('/home') || pathname.startsWith('/room');
}

type SideNavItemProps = {
  label: string;
  icon: JSX.Element;
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
};

function SideNavItem({ label, icon, active, disabled, onClick }: SideNavItemProps) {
  return (
    <TooltipProvider
      position="Right"
      align="Center"
      tooltip={
        <Tooltip>
          <span style={{ fontSize: '13px' }}>{label}</span>
        </Tooltip>
      }
    >
      {(ref) => (
        <button
          ref={ref}
          onClick={disabled ? undefined : onClick}
          title={label}
          className="curve-btn"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '44px',
            height: '44px',
            borderRadius: 'var(--curve-radius-lg, 12px)',
            background: active ? 'var(--curve-primary, #22C55E)' : 'transparent',
            color: disabled
              ? 'var(--curve-nav-icon-disabled, #475569)'
              : active
              ? 'var(--curve-nav-icon-active, #FFFFFF)'
              : 'var(--curve-nav-icon-default, #94A3B8)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.45 : 1,
            transition: 'background 0.15s, color 0.15s',
            outline: 'none',
            border: 'none',
          }}
          onMouseEnter={(e) => {
            if (!active && !disabled) {
              (e.currentTarget as HTMLButtonElement).style.background = 'var(--curve-nav-hover-bg, rgba(255,255,255,0.1))';
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--curve-nav-hover-color, #E2E8F0)';
            }
          }}
          onMouseLeave={(e) => {
            if (!active && !disabled) {
              (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--curve-nav-icon-default, #94A3B8)';
            }
          }}
        >
          {icon}
        </button>
      )}
    </TooltipProvider>
  );
}

export function MainNavSidebar() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const mx = useMatrixClient();
  const { canShowPriority } = useRoomRoleAccessing(mx);
  const messagesActive = isMessagesActive(location.pathname);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleLogout = async () => {
    if (
      await confirmDialog(
        t('Navigation.logout_confirm_title'),
        t('Navigation.logout_confirm_desc'),
        t('Navigation.logout_confirm_btn'),
        'danger'
      )
    ) {
      logoutClient(mx);
    }
  };

  return (
    <div
      style={{
        width: `${NAV_WIDTH}px`,
        minWidth: `${NAV_WIDTH}px`,
        height: '100%',
        background: 'var(--curve-bg-nav, #1E293B)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: '16px',
        paddingBottom: '16px',
        gap: '4px',
        flexShrink: 0,
        zIndex: 20,
        boxShadow: 'var(--curve-nav-inset-shadow)',
      }}
    >
      <SideNavItem
        label={t('Navigation.messages')}
        icon={<Icon src={Icons.Message} size="400" />}
        active={messagesActive}
        onClick={() => navigate(getHomePath())}
      />

      <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <SideNavItem
          label={t('Navigation.settings')}
          icon={<Icon src={Icons.Setting} size="400" />}
          onClick={() => setSettingsOpen(true)}
        />
        {(canShowPriority || !buildConfig.enableWidgetMode) && (
          <SideNavItem
            label={t('Navigation.logout')}
            icon={<Icon src={Icons.Power} size="400" />}
            onClick={handleLogout}
          />
        )}
      </div>

      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
}
